import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import ExploreView from './components/ExploreView';
import AnalyticsView from './components/AnalyticsView';
import BudgetView from './components/BudgetView';
import Topbar, { TOPBAR_HEIGHT } from './components/Topbar';
import LeftSidebar, { SIDEBAR_WIDTH } from './components/LeftSidebar';
import { MOBILE_NAV_HEIGHT } from './components/Sidebar';
import { authAPI, setTokens, getAccessToken, clearTokens } from './api/client';
import { LayoutDashboard, BarChart2, Bell, HelpCircle } from 'lucide-react';
import SettingsModal from './components/SettingsModal';
import { useAnalytics } from './hooks/useAnalytics';
import { ThemeProvider, useTokens } from './context/ThemeContext';
import OnboardingTour, { TOURS, isTourSeen } from './components/OnboardingTour';

// Inject responsive CSS once (media queries can't be done with inline styles)
const GLOBAL_CSS = `
  @keyframes spin { to { transform: rotate(360deg); } }

  .nura-sidebar { display: flex !important; }
  .nura-mobile-nav { display: none !important; }
  .nura-main { padding-top: ${TOPBAR_HEIGHT}; padding-bottom: 0; margin-left: ${SIDEBAR_WIDTH}; box-sizing: border-box; }

  @media (max-width: 640px) {
    .nura-sidebar { display: none !important; }
    .nura-mobile-nav { display: flex !important; }
    .nura-main { padding-top: ${TOPBAR_HEIGHT}; padding-bottom: ${MOBILE_NAV_HEIGHT}; margin-left: 0; box-sizing: border-box; }
  }
`;

function injectGlobalCss() {
  if (document.getElementById('nura-global-css')) return;
  const el = document.createElement('style');
  el.id = 'nura-global-css';
  el.textContent = GLOBAL_CSS;
  document.head.appendChild(el);
}

const MOBILE_NAV_ITEMS = [
  { path: '/transactions', label: 'Transactions', Icon: LayoutDashboard },
  { path: '/analytics',    label: 'Analytics',    Icon: BarChart2 },
  { path: '/budgets',      label: 'Budgets',      Icon: Bell },
];

function AppInner() {
  const t = useTokens();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { track } = useAnalytics();
  const [activeTour, setActiveTour] = useState(null); // 'transactions' | 'analytics' | 'budgets' | null
  const tourAutoShown = useRef(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState('profile');

  const openSettings = (section = 'profile') => {
    setSettingsSection(section);
    setSettingsOpen(true);
  };

  useEffect(() => {
    injectGlobalCss();
    handleAuthCallback();
  }, []);

  // After auth resolves, auto-start tour if not yet seen (fires once per session)
  useEffect(() => {
    if (loading || !user) return;
    if (tourAutoShown.current) return;
    tourAutoShown.current = true;
    if (!isTourSeen('transactions')) {
      navigate('/transactions');
      setTimeout(() => setActiveTour('transactions'), 300);
    }
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('accessToken');
    const refreshToken = urlParams.get('refreshToken');
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error, urlParams.get('reason'));
    }

    const isOAuthCallback = !!(accessToken && refreshToken);
    const isNewUser = urlParams.get('isNewUser') === 'true';
    if (isOAuthCallback) {
      setTokens(accessToken, refreshToken);
    }

    if (urlParams.toString()) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkAuth(isOAuthCallback && isNewUser);
  };

  const checkAuth = async (isOAuthCallback = false) => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getCurrentUser();
      if (response.data.isAuthenticated) {
        let userData = response.data;
        if (isOAuthCallback) {
          const detectedCurrency = sessionStorage.getItem('detectedCurrency');
          sessionStorage.removeItem('detectedCurrency');
          if (detectedCurrency && detectedCurrency !== 'INR' && userData.baseCurrency === 'INR') {
            try {
              const updated = await authAPI.updateProfile({ baseCurrency: detectedCurrency });
              userData = { ...userData, ...updated.data };
            } catch (e) {
              console.error('Failed to set detected currency:', e);
            }
          }
        }
        setUser(userData);
      } else {
        clearTokens();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearTokens();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    track('logout');
    clearTokens();
    setUser(null);
    navigate('/login');
  };

  const handleUserUpdate = (updatedUser) => {
    setUser({ ...user, ...updatedUser });
  };

  const handleLoginSuccess = (userData) => {
    track('login');
    setUser(userData);
    navigate('/transactions');
    // Tour auto-start is handled by the useEffect that watches loading+user
  };

  const handleStartTour = (tourId) => {
    setActiveTour(tourId);
    // Navigate to the right view if needed
    const pathMap = { transactions: '/transactions', analytics: '/analytics', budgets: '/budgets' };
    if (pathMap[tourId] && location.pathname !== pathMap[tourId]) {
      navigate(pathMap[tourId]);
      setTimeout(() => setActiveTour(tourId), 300);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '16px', color: t.textSecondary, gap: '16px', background: t.bgGradient }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${t.border}`, borderTopColor: t.textSlate, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p>Loading…</p>
      </div>
    );
  }

  // Auth routes — accessible when not logged in
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} initialMode="login" />} />
        <Route path="/signup" element={<Login onLoginSuccess={handleLoginSuccess} initialMode="signup" />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: t.bgGradient }}>
      {/* Top bar — full width, always visible on desktop */}
      <Topbar
        user={user}
        onLogout={handleLogout}
        onUserUpdate={handleUserUpdate}
        onOpenSettings={openSettings}
      />

      {/* LHS sidebar — hidden on mobile */}
      <div className="nura-sidebar">
        <LeftSidebar onStartTour={handleStartTour} />
      </div>

      {/* Mobile bottom nav */}
      <nav className="nura-mobile-nav" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: MOBILE_NAV_HEIGHT, backgroundColor: t.surface, borderTop: `1px solid ${t.border}`, zIndex: 20, alignItems: 'stretch', display: settingsOpen ? 'none' : undefined }}>
        {MOBILE_NAV_ITEMS.map(({ path, label, Icon }) => {
          const isActive = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => { track('nav_click', { view: path }); navigate(path); }}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', border: 'none', backgroundColor: 'transparent', color: isActive ? t.navActiveText : t.textMuted, cursor: 'pointer', padding: '8px 0' }}
            >
              <Icon size={20} />
              <span style={{ fontSize: '10px', fontWeight: '500' }}>{label}</span>
            </button>
          );
        })}
        <button
          onClick={() => {
            const tourId = location.pathname.includes('analytics') ? 'analytics' : location.pathname.includes('budgets') ? 'budgets' : 'transactions';
            handleStartTour(tourId);
          }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', border: 'none', backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer', padding: '8px 0' }}
        >
          <HelpCircle size={20} />
          <span style={{ fontSize: '10px', fontWeight: '500' }}>Help</span>
        </button>
      </nav>

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={user}
        onUserUpdate={handleUserUpdate}
        onLogout={handleLogout}
        initialSection={settingsSection}
        renderMobileNav={() => (
          <nav style={{ display: 'flex', borderTop: `1px solid ${t.border}`, backgroundColor: t.surface, flexShrink: 0, paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {MOBILE_NAV_ITEMS.map(({ path, label, Icon }) => (
              <button
                key={path}
                onClick={() => { track('nav_click', { view: path }); navigate(path); setSettingsOpen(false); }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', border: 'none', backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer', padding: '8px 0' }}
              >
                <Icon size={20} />
                <span style={{ fontSize: '10px', fontWeight: '500' }}>{label}</span>
              </button>
            ))}
            <button
              onClick={() => {
                const tourId = location.pathname.includes('analytics') ? 'analytics' : location.pathname.includes('budgets') ? 'budgets' : 'transactions';
                setSettingsOpen(false);
                handleStartTour(tourId);
              }}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', border: 'none', backgroundColor: 'transparent', color: t.textMuted, cursor: 'pointer', padding: '8px 0' }}
            >
              <HelpCircle size={20} />
              <span style={{ fontSize: '10px', fontWeight: '500' }}>Help</span>
            </button>
          </nav>
        )}
      />

      {/* Onboarding tour overlay */}
      {activeTour && TOURS[activeTour] && (
        <OnboardingTour
          tourId={activeTour}
          steps={TOURS[activeTour]}
          active={!!activeTour}
          onClose={() => setActiveTour(null)}
        />
      )}

      {/* Main scrollable content */}
      <main className="nura-main" style={{ height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '28px 24px', display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="/transactions" element={<ExploreView baseCurrency={user?.baseCurrency || 'INR'} customCategories={user?.customCategories || []} excludedCategories={user?.excludedCategories || []} />} />
            <Route path="/analytics" element={<AnalyticsView baseCurrency={user?.baseCurrency || 'INR'} excludedCategories={user?.excludedCategories || []} />} />
            <Route path="/budgets" element={<BudgetView baseCurrency={user?.baseCurrency || 'INR'} />} />
            <Route path="/profile" element={<Navigate to="/transactions" replace />} />
            <Route path="/" element={<Navigate to="/transactions" replace />} />
            <Route path="*" element={<Navigate to="/transactions" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
